// Environment configuration for React app
// This replaces the Angular environment.ts structure

interface Environment {
  CAFEEncryptionKey: string;
  appRoot: string;
  production: boolean;
  loginUrl: string;
  apiPrefix: {
    rootPath: string;
    coreRootPath: string;
    connect: string;
  };
  logo: string;
  smartyStreetConnectEndpoint: string;
  GetAuthenticationTypeUrl: string;
  homepageRoute: string;
  productName: string;
  favIcon: string;
  appMode: string;
  EnableDarkModeToggle: boolean;
  logoImageURL: string;
  bnnerImageURL: string;
  GoogleAuthClientId: string;
  GoogleLoginURI: string;
  AppleAuthClientId: string;
  AppleLoginURI: string;
  MicrosoftAuthClientId: string;
  MicrosoftLoginURI: string;
  OLERoutePath: string;
  RecaptchaSiteKey: string;
  RecaptchaV2SiteKey: string;
  ENABLE_ROUTE_PROTECTION: boolean;
  // Additional authentication flags
  googleAuthEnabled: boolean;
  microsoftAuthEnabled: boolean;
  appleAuthEnabled: boolean;
  magicLinkEnabled: boolean;
  otpEnabled: boolean;
  multipleSecureLoginOptions: boolean;
  // Local Storage Keys
  localStorageKeys: {
    propertyId: string;
    siteName: string;
    floorPlanId: string;
    selectedUnitId: string;
    selectedUnitCode: string;
    selectedArea: string;
    selectedAreaDisplay: string;
    selectedBedCount: string;
    selectedBathCount: string;
    selectedMoveInDate: string;
    lastCompletedStep: string;
    selectedRoomID: string;
    selectedRoomCode: string;
  };
}

// Helper function to get environment variable with fallback
const getEnvVar = (key: string, fallback: string = ''): string => {
  return import.meta.env[key] || fallback;
};

const getBooleanEnvVar = (key: string, fallback: boolean = false): boolean => {
  const value = import.meta.env[key];
  if (value === undefined) return fallback;
  return value === 'true';
};

export const environment: Environment = {
  CAFEEncryptionKey: 'ff9sjdfjd4##4f9dFdnc.d',
  appRoot: getEnvVar('VITE_APP_ROOT', '/onlineleasingstudent'),
  production: getBooleanEnvVar('VITE_PRODUCTION', false),
  loginUrl: '/onlineleasingstudent/content3/access/login',
  apiPrefix: {
    // Use Vite proxy URLs to avoid CORS issues - all requests go through localhost:3000
    rootPath: getEnvVar('VITE_API_PREFIX', '/onlineleasingstudent/api/'),
    coreRootPath: getEnvVar('VITE_API_PREFIX', '/onlineleasingstudent/api/'),
    connect: getEnvVar('VITE_API_PREFIX', '/onlineleasingstudent/api/') + 'connect/',
  },
  logo: "/assets/images/rentcafeole_blue.png",
  smartyStreetConnectEndpoint: "smartystreets/autocomplete",
  GetAuthenticationTypeUrl: '/api/common/GetAuthenticationType',
  homepageRoute: getEnvVar('VITE_HOMEPAGE_ROUTE', '/content3'),
  productName: "onlineleasingstudent",
  favIcon: "/assets/images/favicon_rentcafeolemanager.ico",
  appMode: "onlineleasingstudent",
  EnableDarkModeToggle: true,
  logoImageURL: getEnvVar('VITE_LOGO_IMAGE_URL', "https://cdngeneralcf.rcqatol.com/dmslivecafe/2/61426/co%20logo.png?&quality=75"),
  bnnerImageURL: getEnvVar('VITE_BANNER_IMAGE_URL', "/assets/images/Carina_bannerimage_default.jpg"),
  GoogleAuthClientId: "531124302354-ga9gulhqvtobbpj5job4lsmj9pa3hh4a.apps.googleusercontent.com",
  GoogleLoginURI: getEnvVar('VITE_GOOGLE_LOGIN_URI', "https://www.securecafeapplicant.com/onlineleasingstudent/api/login/google"),
  MicrosoftAuthClientId: "8d171c1b-fee2-4f2e-9f93-3521b4d5da0d",
  MicrosoftLoginURI: "https://www.securecafeapplicant.com/onlineleasingstudent/api/login/microsoft",
  AppleAuthClientId: getEnvVar('VITE_APPLE_AUTH_CLIENT_ID', "com.resportal.service"),
  AppleLoginURI: getEnvVar('VITE_APPLE_LOGIN_URI', "https://www.securecafeapplicant.com/onlineleasingstudent/api/login/apple"),
  OLERoutePath: getEnvVar('VITE_OLE_ROUTE_PATH', "/ole/"),
    RecaptchaSiteKey: getEnvVar('VITE_RECAPTCHA_SITE_KEY', "6LfBeqEaAAAAALsbENKGUsE98xFoA3ZpqkbzogBI"),
    RecaptchaV2SiteKey: getEnvVar('VITE_RECAPTCHA_V2_SITE_KEY', "6Led3AcTAAAAAMU9N0MWjGzH1EM2ewS_DHHVol3p"),
  ENABLE_ROUTE_PROTECTION: getBooleanEnvVar('VITE_ENABLE_ROUTE_PROTECTION', true),
  
  // Authentication feature flags
  googleAuthEnabled: getBooleanEnvVar('VITE_GOOGLE_AUTH_ENABLED', true),
  microsoftAuthEnabled: getBooleanEnvVar('VITE_MICROSOFT_AUTH_ENABLED', true),
  appleAuthEnabled: getBooleanEnvVar('VITE_APPLE_AUTH_ENABLED', true),
  magicLinkEnabled: getBooleanEnvVar('VITE_MAGIC_LINK_ENABLED', true),
  otpEnabled: getBooleanEnvVar('VITE_OTP_ENABLED', true),
  multipleSecureLoginOptions: getBooleanEnvVar('VITE_MULTIPLE_SECURE_LOGIN_OPTIONS', true),
  
  // Local Storage Keys
  localStorageKeys: {
    propertyId: getEnvVar('VITE_PROPERTY_ID_KEY', 'PropertyId'),
    siteName: getEnvVar('VITE_SITE_NAME_KEY', 'sitename'),
    floorPlanId: getEnvVar('VITE_FLOOR_PLAN_ID_KEY', 'FloorPlanID'),
    selectedUnitId: getEnvVar('VITE_SELECTED_UNIT_ID_KEY', 'SelectedUnitID'),
    selectedUnitCode: getEnvVar('VITE_SELECTED_UNIT_CODE_KEY', 'SelectedUnitCode'),
    selectedArea: getEnvVar('VITE_SELECTED_AREA_KEY', 'SelectedArea'),
    selectedAreaDisplay: getEnvVar('VITE_SELECTED_AREA_DISPLAY_KEY', 'SelectedAreaDisplay'),
    selectedBedCount: getEnvVar('VITE_SELECTED_BED_COUNT_KEY', 'SelectedBedCount'),
    selectedBathCount: getEnvVar('VITE_SELECTED_BATH_COUNT_KEY', 'SelectedBathCount'),
    selectedMoveInDate: getEnvVar('VITE_SELECTED_MOVE_IN_DATE_KEY', 'SelectedMoveInDate'),
    lastCompletedStep: getEnvVar('VITE_LAST_COMPLETED_STEP_KEY', 'LastCompletedStepPagenName'),
    selectedRoomID: getEnvVar('VITE_SELECTED_ROOM_ID_KEY', 'SelectedRoomID'),
    selectedRoomCode: getEnvVar('VITE_SELECTED_ROOM_CODE_KEY', 'SelectedRoomCode'),
  },
};

// Export individual values for easier importing
export const {
  apiPrefix,
  appRoot,
  homepageRoute,
  OLERoutePath,
  RecaptchaSiteKey,
  GoogleAuthClientId,
  logoImageURL,
  bnnerImageURL,
  googleAuthEnabled,
  microsoftAuthEnabled,
  appleAuthEnabled,
  magicLinkEnabled,
  otpEnabled,
  multipleSecureLoginOptions
} = environment;

// Global Constants (equivalent to Angular GlobalConstants)
export const GlobalConstants = {
  INVALID_SESSION_ERROR: 'INVALID_SESSION_ERROR'
};

